import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  SUPPORTED_LOCALES,
  translate,
  type AppLocale,
  type MessageKey,
  type MessageParameters,
} from "./translations.ts";

export {
  SUPPORTED_LOCALES,
  translate,
  type AppLocale,
  type MessageKey,
  type MessageParameters,
} from "./translations.ts";

export const DEFAULT_LOCALE: AppLocale = "ar-SA";
export const LOCALE_STORAGE_KEY = "tu-conferences-locale";

export type TextDirection = "rtl" | "ltr";

export const LOCALE_METADATA = Object.freeze({
  "ar-SA": Object.freeze({
    direction: "rtl",
    documentLanguage: "ar",
    intlLocale: "ar-SA",
  }),
  en: Object.freeze({
    direction: "ltr",
    documentLanguage: "en",
    intlLocale: "en",
  }),
}) satisfies Readonly<
  Record<
    AppLocale,
    Readonly<{
      direction: TextDirection;
      documentLanguage: string;
      intlLocale: string;
    }>
  >
>;

export interface LocaleStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface LocaleDocumentTarget {
  lang: string;
  dir: string;
  dataset: { locale?: string };
}

export function isSupportedLocale(value: unknown): value is AppLocale {
  return (
    typeof value === "string" &&
    (SUPPORTED_LOCALES as readonly string[]).includes(value)
  );
}

function availableBrowserStorage(): LocaleStorage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

function availableDocumentTarget(): LocaleDocumentTarget | undefined {
  return typeof document === "undefined" ? undefined : document.documentElement;
}

export function readStoredLocale(
  storage: LocaleStorage | undefined = availableBrowserStorage(),
): AppLocale | null {
  if (!storage) return null;
  try {
    const storedLocale = storage.getItem(LOCALE_STORAGE_KEY);
    return isSupportedLocale(storedLocale) ? storedLocale : null;
  } catch {
    return null;
  }
}

function localeFromDocument(
  target: LocaleDocumentTarget | undefined,
): AppLocale | null {
  if (!target) return null;
  if (isSupportedLocale(target.dataset.locale)) return target.dataset.locale;
  return target.lang.toLowerCase().startsWith("en") ? "en" : null;
}

export function resolveInitialLocale(
  storage: LocaleStorage | undefined = availableBrowserStorage(),
  target: LocaleDocumentTarget | undefined = availableDocumentTarget(),
): AppLocale {
  return (
    readStoredLocale(storage) ?? localeFromDocument(target) ?? DEFAULT_LOCALE
  );
}

export function persistLocale(
  locale: AppLocale,
  storage: LocaleStorage | undefined = availableBrowserStorage(),
): boolean {
  if (!storage) return false;
  try {
    storage.setItem(LOCALE_STORAGE_KEY, locale);
    return true;
  } catch {
    return false;
  }
}

export function applyLocaleToDocument(
  locale: AppLocale,
  target: LocaleDocumentTarget | undefined = availableDocumentTarget(),
): void {
  if (!target) return;
  const metadata = LOCALE_METADATA[locale];
  target.lang = metadata.documentLanguage;
  target.dir = metadata.direction;
  target.dataset.locale = locale;
}

export function formatNumber(
  value: number | bigint,
  locale: AppLocale,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(LOCALE_METADATA[locale].intlLocale, {
    ...options,
    numberingSystem: "latn",
  }).format(value);
}

export function formatCurrencyParts(
  value: number | bigint,
  currency: string,
  locale: AppLocale,
  options: Omit<Intl.NumberFormatOptions, "style" | "currency"> = {},
): readonly Intl.NumberFormatPart[] {
  return new Intl.NumberFormat(LOCALE_METADATA[locale].intlLocale, {
    ...options,
    style: "currency",
    currency,
    numberingSystem: "latn",
  }).formatToParts(value);
}

export function formatCurrency(
  value: number | bigint,
  currency: string,
  locale: AppLocale,
  options: Omit<Intl.NumberFormatOptions, "style" | "currency"> = {},
): string {
  return formatCurrencyParts(value, currency, locale, options)
    .map(({ value: part }) => part)
    .join("");
}

export function formatPercent(
  value: number,
  locale: AppLocale,
  options: Omit<Intl.NumberFormatOptions, "style"> = {},
): string {
  return new Intl.NumberFormat(LOCALE_METADATA[locale].intlLocale, {
    ...options,
    style: "percent",
    numberingSystem: "latn",
  }).format(value);
}

export function formatDate(
  value: Date | number | string,
  locale: AppLocale,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(LOCALE_METADATA[locale].intlLocale, {
    ...options,
    numberingSystem: "latn",
  }).format(date);
}

export function formatDateRange(
  start: Date | number | string,
  end: Date | number | string,
  locale: AppLocale,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const formatter = new Intl.DateTimeFormat(
    LOCALE_METADATA[locale].intlLocale,
    { ...options, numberingSystem: "latn" },
  );
  return formatter.formatRange(
    start instanceof Date ? start : new Date(start),
    end instanceof Date ? end : new Date(end),
  );
}

const FIRST_STRONG_ISOLATE = "\u2068";
const LEFT_TO_RIGHT_ISOLATE = "\u2066";
const POP_DIRECTIONAL_ISOLATE = "\u2069";

export function isolateText(value: string | number): string {
  return `${FIRST_STRONG_ISOLATE}${String(value)}${POP_DIRECTIONAL_ISOLATE}`;
}

export function isolateLtr(value: string | number): string {
  return `${LEFT_TO_RIGHT_ISOLATE}${String(value)}${POP_DIRECTIONAL_ISOLATE}`;
}

export function bidiIsolationAttributes(
  direction: TextDirection = "ltr",
): Readonly<{
  dir: TextDirection;
  style: Readonly<{ unicodeBidi: "isolate" }>;
}> {
  return Object.freeze({
    dir: direction,
    style: Object.freeze({ unicodeBidi: "isolate" as const }),
  });
}

export interface LocaleContextValue {
  readonly locale: AppLocale;
  readonly direction: TextDirection;
  readonly setLocale: (locale: AppLocale) => void;
  readonly t: (key: MessageKey, parameters?: MessageParameters) => string;
  readonly number: (
    value: number | bigint,
    options?: Intl.NumberFormatOptions,
  ) => string;
  readonly currency: (
    value: number | bigint,
    currency: string,
    options?: Omit<Intl.NumberFormatOptions, "style" | "currency">,
  ) => string;
  readonly percent: (
    value: number,
    options?: Omit<Intl.NumberFormatOptions, "style">,
  ) => string;
  readonly date: (
    value: Date | number | string,
    options?: Intl.DateTimeFormatOptions,
  ) => string;
  readonly dateRange: (
    start: Date | number | string,
    end: Date | number | string,
    options?: Intl.DateTimeFormatOptions,
  ) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  children,
  initialLocale,
}: Readonly<{
  children: ReactNode;
  initialLocale?: AppLocale;
}>): ReactElement {
  const [locale, setLocaleState] = useState<AppLocale>(
    () => initialLocale ?? resolveInitialLocale(),
  );

  const setLocale = useCallback((nextLocale: AppLocale) => {
    setLocaleState(nextLocale);
  }, []);

  useLayoutEffect(() => {
    applyLocaleToDocument(locale);
    persistLocale(locale);
    document.title = translate(locale, "app.documentTitle");
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      direction: LOCALE_METADATA[locale].direction,
      setLocale,
      t: (key, parameters) => translate(locale, key, parameters),
      number: (numberValue, options) =>
        formatNumber(numberValue, locale, options),
      currency: (currencyValue, currencyCode, options) =>
        formatCurrency(currencyValue, currencyCode, locale, options),
      percent: (percentValue, options) =>
        formatPercent(percentValue, locale, options),
      date: (dateValue, options) => formatDate(dateValue, locale, options),
      dateRange: (start, end, options) =>
        formatDateRange(start, end, locale, options),
    }),
    [locale, setLocale],
  );

  return createElement(LocaleContext.Provider, { value }, children);
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within LocaleProvider");
  }
  return context;
}
